import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { AxiosError } from 'axios';

import { StockItemPrice } from 'src/components/StockItemPrice';
import {
  ProductCard,
  ProductCharacteristic,
  useProduct,
} from 'src/entities/product';
import { certificateSummaryLabels } from 'src/entities/certificate';
import { useRouter } from 'src/hooks/useRouter';
import { ProductImage } from 'src/types/api';

import { RichDescription } from './RichDescription';

const CERTIFICATION_META: Record<
  string,
  { label: string; color: 'default' | 'success' | 'warning' | 'error' }
> = {
  not_required: { label: 'Сертификация не требуется', color: 'default' },
  present: { label: 'Сертификат есть', color: 'success' },
  missing: { label: 'Сертификат отсутствует', color: 'error' },
};

/**
 * «Сертификат есть» ≠ «сертификат в порядке»: present с проблемами
 * (протух/приостановлен/не найден/не проверен) показываем предупреждением.
 */
const certificationMeta = (product: ProductCard) => {
  if (
    product.certificationStatus === 'present' &&
    product.hasCertificateIssues
  ) {
    return {
      label: 'Сертификат есть, но есть проблемы',
      color: 'warning' as const,
    };
  }
  return product.certificationStatus
    ? CERTIFICATION_META[product.certificationStatus]
    : undefined;
};

type InfoRow = { label: string; value: React.ReactNode };

const imagesOf = (product: ProductCard): ProductImage[] =>
  [product.defaultImage, ...(product.images || [])].filter(
    (image, index, all): image is ProductImage =>
      Boolean(image?.uri) &&
      all.findIndex(other => other?.uri === image?.uri) === index,
  );

const characteristicRows = (product: ProductCard): InfoRow[] => {
  const groups = [
    product.numericCharacteristics,
    product.stringCharacteristics,
    product.boolCharacteristics,
  ];
  const rows: InfoRow[] = [];
  groups.forEach(group => {
    if (!Array.isArray(group)) return;
    (group as ProductCharacteristic[]).forEach(item => {
      const name = item.attributeName;
      if (!name) return;
      const rawValue = item.adaptValue ?? item.value;
      rows.push({
        label: name,
        value:
          rawValue === null || typeof rawValue === 'undefined'
            ? '—'
            : String(rawValue),
      });
    });
  });
  return rows;
};

const textSection = (title: string, value?: string | null) =>
  value ? (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography sx={{ whiteSpace: 'pre-wrap' }}>{value}</Typography>
    </Paper>
  ) : null;

export const ProductPage: React.FunctionComponent = () => {
  const router = useRouter();
  const code = router.query.productCode;

  const productRequest = useProduct({
    variables: { code: String(code ?? '') },
    skip: !code,
  });

  const { data, error } = productRequest;
  const httpStatus = (error as AxiosError | null)?.response?.status;

  if (!code || httpStatus === 404) {
    return <Alert severity="info">Товар не найден</Alert>;
  }

  const isLoading = !data && !error;
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (httpStatus === 401 || httpStatus === 403) {
    return <Alert severity="error">Нет доступа к карточке товара</Alert>;
  }

  if (error) {
    return <Alert severity="error">Не удалось загрузить карточку товара</Alert>;
  }

  if (!data?.data) {
    return <Alert severity="info">Товар не найден</Alert>;
  }

  const product = data.data;
  const images = imagesOf(product);

  const brandName =
    product.brand?.adaptedName && product.brand.adaptedName !== product.brand?.name
      ? `${product.brand.name} (${product.brand.adaptedName})`
      : product.brand?.name;

  const mainRows: InfoRow[] = [
    { label: 'Код', value: product.code },
    { label: 'Артикул', value: product.vendorCode },
    {
      label: 'Штрихкоды',
      value: product.barcodes?.length ? product.barcodes.join(', ') : null,
    },
    { label: 'Бренд', value: brandName },
    { label: 'Серия', value: product.series?.name },
    { label: 'Производитель', value: product.manufacturer?.name },
    { label: 'Категория', value: product.category?.name },
  ];

  const visibleMainRows = mainRows.filter(
    row =>
      row.value !== null &&
      row.value !== undefined &&
      row.value !== '' &&
      row.value !== false,
  );

  const characteristics = characteristicRows(product);
  const stockRows = (product.stockItems || []).filter(
    item => Boolean(item) && Boolean(item.warehouse || item.vendorId),
  );
  const cert = certificationMeta(product);
  const certificateSummary = product.certificatesSummary;
  const certificateSummaryRows = certificateSummary
    ? Object.entries(certificateSummary)
        .filter(
          ([key, value]) =>
            key !== 'total' && typeof value === 'number' && value > 0,
        )
        .map(([key, value]) => ({
          label: certificateSummaryLabels[key] || key,
          value,
        }))
    : [];

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {images.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                flexShrink: 0,
              }}
            >
              {images.slice(0, 4).map(image => (
                <a
                  key={image.uri}
                  href={image.uri}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Box
                    component="img"
                    src={`${image.uri}?w=120`}
                    alt={product.title || 'Фото товара'}
                    sx={{
                      width: 96,
                      height: 96,
                      objectFit: 'contain',
                      bgcolor: 'white',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  />
                </a>
              ))}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" gutterBottom>
              {product.title}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {typeof product.code !== 'undefined' && (
                <Chip size="small" label={`Код: ${product.code}`} variant="outlined" />
              )}
              {product.category?.name && (
                <Chip size="small" label={product.category.name} variant="outlined" />
              )}
              {cert && <Chip size="small" color={cert.color} label={cert.label} />}
            </Box>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Основное
        </Typography>
        <Table size="small">
          <TableBody>
            {visibleMainRows.map(row => (
              <TableRow key={row.label}>
                <TableCell sx={{ width: 220, color: 'text.secondary' }}>
                  {row.label}
                </TableCell>
                <TableCell>{row.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {certificateSummary ? (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Сертификация
          </Typography>
          {cert && (
            <Chip
              size="small"
              color={cert.color}
              label={cert.label}
              sx={{ mb: certificateSummaryRows.length ? 2 : 0 }}
            />
          )}
          {certificateSummaryRows.length > 0 && (
            <Table size="small">
              <TableBody>
                {certificateSummaryRows.map(row => (
                  <TableRow key={row.label}>
                    <TableCell sx={{ width: 220, color: 'text.secondary' }}>
                      {row.label}
                    </TableCell>
                    <TableCell>{row.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {typeof certificateSummary.total === 'number' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Всего привязок: {certificateSummary.total}
            </Typography>
          )}
        </Paper>
      ) : null}

      {textSection('Описание', product.description)}
      {textSection('Способ применения', product.howToUse)}
      {textSection('Состав', product.ingredients)}

      {characteristics.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Характеристики
          </Typography>
          <Table size="small">
            <TableBody>
              {characteristics.map(row => (
                <TableRow key={row.label}>
                  <TableCell sx={{ width: 220, color: 'text.secondary' }}>
                    {row.label}
                  </TableCell>
                  <TableCell>{row.value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {stockRows.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Остатки и цены по складам
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Склад</TableCell>
                <TableCell align="right">Цена</TableCell>
                <TableCell align="right">Остаток</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stockRows.map((item, index) => (
                <TableRow key={item.id ?? index}>
                  <TableCell>
                    {item.warehouse?.name ?? item.vendorId ?? '—'}
                  </TableCell>
                  <TableCell align="right">
                    <StockItemPrice offerPrice={item.offerPrice} />
                  </TableCell>
                  <TableCell align="right">
                    {typeof item.publicStock === 'number'
                      ? item.publicStock
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {product.richDescription?.blocks?.length ? (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Rich-описание
          </Typography>
          <RichDescription payload={product.richDescription} />
        </Paper>
      ) : null}
    </Box>
  );
};
